'use client';
import SupportOfferCard from '@/components/OfferCards/SupportOfferCard';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Spacer from '@/components/ui/Spacer';
import { useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useState } from 'react';
import useService from '@/hooks/useService';
import { SupportService } from '@/service/SupportService';
import { decodeUrlParam, toast } from '@/utils';
import { formatServerErrors } from '@/utils/withDefaultUiHandler';
import TicketRaisedDialog from './components/TicketRaisedDialog';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@/constants/routes.const';
import FileInput from '@/components/ui/FileInput';
import { AndroidService } from '@/service/Android';

const initialFormValues = {
  subject: '',
  desc: '',
  email: '',
  attachments: []
};

const MAX_FILES = 3;
const MAX_FILE_SIZE_MB = 20;
export default function RaiseSupportTicket() {
  useEffect(() => {
    AndroidService.logEvent('support_ticket_open', null);
  }, []);

  const searchParams = useSearchParams();
  const router = useRouter();
  const offerParam = searchParams.get('offer');
  const subject = searchParams.get('subject');
  const [isTicketRaised, setTicketRaised] = useState(false);
  const offer = !!offerParam ? decodeUrlParam(offerParam) : null;

  const [formValues, setFormValues] = useState({
    ...initialFormValues,
    subject: subject ?? ''
  });
  const raiseSupportTicket = useService(SupportService.raiseSupportTicket);

  async function onRaiseSupportTicket() {
    const { subject, desc, email } = formValues;
    const warnings = validate(formValues);

    if (warnings.length > 0) {
      warnings.forEach(warning => {
        toast(warning);
      });
      return;
    }

    try {
      await raiseSupportTicket.call({
        email,
        subject,
        message: desc,
        attachments: formValues?.attachments ?? [],
        ...(!!offer && { offerId: offer.offerId }),
        rewardId: 'rewardId'
      });

      setTicketRaised(true);
      setFormValues(initialFormValues);
    } catch (err) {
      toast(formatServerErrors(err).join(' '));
    }
  }

  return (
    <Suspense>
      <div className="flex flex-col h-[calc(100%_-_100px)] px-4 landscape:px-14">
        {!!offer && (
          <>
            <SupportOfferCard offer={offer} />
            <Spacer size="large" />
          </>
        )}

        <h2 className="py-2 font-bold landscape:py-4">
          Tell us about your issue
        </h2>
        <div className="grid gap-3">
          <Input
            label={'Email'}
            type="email"
            value={formValues?.email}
            onChange={e => {
              setFormValues(prev => ({ ...prev, email: e.target.value }));
            }}
          />
          <Input
            label={'Subject'}
            value={formValues?.subject}
            onChange={e => {
              setFormValues(prev => ({ ...prev, subject: e.target.value }));
            }}
          />

          <Input
            label={'Tell us about your issue'}
            value={formValues?.desc}
            onChange={e => {
              setFormValues(prev => ({ ...prev, desc: e.target.value }));
            }}
            className="min-h-[200px]"
            type="textarea"
          />
          {/* file attachments */}
          <Input
            label={`Upload attachments (max file size: ${MAX_FILE_SIZE_MB}MB)`}
            type="file"
            inputJsx={
              <FileInput
                files={formValues?.attachments}
                // image , video
                accept="image/*,video/*"
                maxFiles={MAX_FILES}
                multiple
                onRemove={index => {
                  setFormValues(prev => ({
                    ...prev,
                    attachments: prev.attachments.filter((_, i) => i !== index)
                  }));
                }}
                onChange={({ target }) => {
                  if (!target.files?.length) return;
                  const files = [...target.files];
                  // reset input value
                  // fix: upload same file again, onChange not triggered
                  // ... upload, delete, upload same file again , onChange not triggered
                  target.value = '';
                  //  check file size
                  const invalidFiles = files.filter(
                    file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024
                  );
                  if (invalidFiles.length) {
                    toast(
                      `File size should be less than ${MAX_FILE_SIZE_MB}MB`
                    );
                    return;
                  }

                  setFormValues(prev => ({
                    ...prev,
                    attachments: [...prev.attachments, ...files].slice(
                      0,
                      MAX_FILES
                    )
                  }));
                  return false;
                }}
              />
            }
          />
          <Spacer />
        </div>
        <div className="flex justify-center w-full mt-auto landscape:py-6">
          <Button
            className="w-[90%] py-4 landscape:w-[30%]"
            color="primary"
            variant="contained"
            isLoading={raiseSupportTicket.isLoading}
            onClick={onRaiseSupportTicket}
            disabled={
              raiseSupportTicket.isLoading ||
              isTicketRaised ||
              !formValues.email ||
              !formValues.subject ||
              !formValues.desc
            }
          >
            Send query
          </Button>
        </div>
      </div>
      <TicketRaisedDialog
        isOpen={isTicketRaised}
        onOpenChange={() => {
          setTicketRaised(false);
          router.replace(ROUTES.HOME);
        }}
      />
    </Suspense>
  );
}

function validate({ email, subject, desc }) {
  const warnings = [];
  if (!email) {
    warnings.push('Email is required');
  } else if (!validateEmail(email)) {
    warnings.push('Invalid email');
  }
  if (!subject) {
    warnings.push('Subject is required');
  }
  if (!desc) {
    warnings.push('Description is required');
  }
  return warnings;
}

function validateEmail(email) {
  const re = /\S+@\S+\.\S{2,}/;
  return re.test(email);
}
